import React, {Component} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {SelectInput} from "../../components";
import FormLanguages from "./enum/FormLanguages";
import RemotingService from "../../services/remoting-service/RemotingService";
import Tooltip from "../../components/tooltip/Tooltip";

export default class ConsentFormLink extends Component {
    constructor(props) {
        super(props);
        this.state = {
            copied: false,
            emailStatus: null,
            selectedLanguage: FormLanguages.ENGLISH
        };
        this.linkRef = React.createRef();
    }

    copyToClipboard = async () => {
        if (this.linkRef.current) {
            try {
                await navigator.clipboard.writeText(this.linkRef.current.value);
                this.setState({copied: true});

                setTimeout(() => {
                    this.setState({copied: false});
                }, 2000);
            } catch (err) {
                console.error('Failed to copy: ', err);
            }
        }
    }

    sendEmail = () => {
        const { link } = this.props;

        if (!link) {
            alert("Missing link information");
            return;
        }

        const urlParams = new URLSearchParams(new URL(link).search);
        const secret = urlParams.get('secret');
        const language = this.state.selectedLanguage.key;

        if (!secret) {
            alert("Invalid link format");
            return;
        }

        this.setState({ emailStatus: 'sending' });

        RemotingService.postRemoteCall(
            `api/consent-form/link/send-email?secret=${secret}&language=${language}`,
            null,
            (response) => {
                this.setState({ emailStatus: 'sent' });
            },
            (error) => {
                this.setState({ emailStatus: 'error' });
            }
        );
    }

    getFormattedLink = () => {
        const { link } = this.props;
        const { selectedLanguage } = this.state;

        if (link && link.includes('?')) {
            return `${link}&language=${selectedLanguage.key}`;
        } else if (link) {
            return `${link}?language=${selectedLanguage.key}`;
        }
        return link;
    }

    getEmailButtonContent = () => {
        const { emailStatus } = this.state;

        switch (emailStatus) {
            case 'sending':
                return (
                    <>
                        <FontAwesomeIcon icon={["fas", "spinner"]} spin /> Sending...
                    </>
                );
            case 'sent':
                return (
                    <>
                        <FontAwesomeIcon icon={["fas", "check"]} /> Sent
                    </>
                );
            case 'error':
                return (
                    <>
                        <FontAwesomeIcon icon={["fas", "exclamation-circle"]} /> Failed
                    </>
                );
            default:
                return (
                    <>
                        <FontAwesomeIcon icon={["fas", "envelope"]}/> Email to Patient
                    </>
                );
        }
    }

    getEmailButtonStyle = () => {
        const { emailStatus } = this.state;

        const baseStyle = {
            marginLeft: "8px",
            padding: "5px 10px",
            borderRadius: "4px",
            color: "white",
            border: "none",
            cursor: emailStatus === 'sending' ? "not-allowed" : "pointer",
            transition: "all 0.2s ease-in-out"
        };

        switch (emailStatus) {
            case 'sending':
                return { ...baseStyle, backgroundColor: "#6c757d" };
            case 'sent':
                return {
                    ...baseStyle,
                    backgroundColor: "#7bac16",
                    ':hover': {
                        backgroundColor: "#8bc424"
                    }
                };
            case 'error':
                return {
                    ...baseStyle,
                    backgroundColor: "#dc3545",
                    ':hover': {
                        backgroundColor: "#e74a3b"
                    }
                };
            default:
                return { ...baseStyle, backgroundColor: "#28a745" };
        }
    }

    getTooltipTitle = () => {
        const { emailStatus } = this.state;

        switch (emailStatus) {
            case 'sent':
                return "Click to send again";
            case 'error':
                return "Click to retry";
            default:
                return "";
        }
    }

    render() {
        const { duration, formName } = this.props;
        const { copied, emailStatus, selectedLanguage } = this.state;
        const formattedLink = this.getFormattedLink();

        return (
            <div
                className="display-flex flex-column text-center"
                style={{ paddingTop: "10px" }}
            >
                <div style={{ margin: "10px auto", maxWidth: "80%" }}>
                    <div style={{ fontSize: 12, fontWeight: "bold", marginBottom: "5px", textAlign: "left" }}>
                        Form Language
                    </div>
                    <SelectInput
                        className="patient-consent-language-selection"
                        showClear={false}
                        searchEnabled={false}
                        value={selectedLanguage}
                        options={FormLanguages.toArray()}
                        displayProperty={'name'}
                        onChange={(language) => this.setState({ selectedLanguage: language })}
                    />
                </div>

                <div style={{ fontSize: 15 }}>
                    <b>Consent Form Link</b>
                </div>

                <div className="mt-2">
                    <div style={{display: "flex", justifyContent: "center"}}>
                        <input
                            ref={this.linkRef}
                            readOnly
                            value={formattedLink}
                            style={{
                                width: "90%",
                                padding: "8px",
                                borderRadius: "4px",
                                border: "1px solid #ccc",
                                fontSize: "14px"
                            }}
                        />
                    </div>
                    <div style={{display: "flex", justifyContent: "flex-end", marginTop: "10px", paddingRight: "5%"}}>
                        <button
                            onClick={this.copyToClipboard}
                            style={{
                                padding: "5px 10px",
                                borderRadius: "4px",
                                backgroundColor: copied ? "#7bac16" : "#007bff",
                                color: "white",
                                border: "none",
                                cursor: "pointer"
                            }}
                        >
                            {copied ? (
                                <>
                                    <FontAwesomeIcon icon={["fas", "check"]}/> Copied
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={["fas", "copy"]}/> Copy
                                </>
                            )}
                        </button>
                        <Tooltip title={this.getTooltipTitle()}>
                            <button
                                onClick={this.sendEmail}
                                disabled={emailStatus === 'sending'}
                                style={this.getEmailButtonStyle()}
                                className={emailStatus === 'sent' || emailStatus === 'error' ? 'hover-effect' : ''}
                            >
                                {this.getEmailButtonContent()}
                            </button>
                        </Tooltip>
                    </div>
                </div>
                <div style={{ fontSize: 11, marginTop: "10px" }}>
                    This link is valid for {Math.floor(duration / (60 * 24))} days
                </div>
                <div style={{ fontSize: 11, fontWeight: "bold", marginTop: "10px" }}>
                    Send this link to the patient to complete the form.
                </div>
            </div>
        );
    }
} 